import * as PopoverPrimitive from '@radix-ui/react-popover';
import { ChevronDown, X } from 'lucide-react';
import { KeyboardEvent, ReactNode, Ref, useEffect, useMemo, useRef, useState } from 'react';

import { Chip } from './Chip';
import { cn } from './cn';
import { Spinner } from './Spinner';

export type ComboboxInputChangeReason = 'input' | 'reset' | 'clear';

type ComboboxBaseProps<T> = {
  options: readonly T[];
  getOptionLabel?: (option: T) => string;
  getOptionKey?: (option: T) => string | number;
  isOptionEqualToValue?: (option: T, value: T) => boolean;
  filterOptions?: (options: T[], inputValue: string) => T[];
  renderOption?: (option: T, state: { selected: boolean }) => ReactNode;
  inputValue?: string;
  onInputChange?: (value: string, reason: ComboboxInputChangeReason) => void;
  /** Commits typed text as a value. Only valid when options are strings. */
  freeSolo?: boolean;
  loading?: boolean;
  loadingText?: ReactNode;
  noOptionsText?: ReactNode;
  disableClearable?: boolean;
  placeholder?: string;
  disabled?: boolean;
  invalid?: boolean;
  autoFocus?: boolean;
  id?: string;
  className?: string;
  inputClassName?: string;
  inputRef?: Ref<HTMLInputElement>;
  'aria-label'?: string;
  onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void;
  onBlur?: () => void;
  endAdornment?: ReactNode;
};

export type ComboboxProps<T> = ComboboxBaseProps<T> &
  (
    | {
        multiple?: false;
        value: NoInfer<T> | null;
        onChange: (value: NoInfer<T> | null) => void;
      }
    | {
        multiple: true;
        value: NoInfer<T>[];
        onChange: (value: NoInfer<T>[]) => void;
        renderTag?: (option: T, onDelete: () => void) => ReactNode;
      }
  );

let comboboxCount = 0;

const defaultGetOptionLabel = (option: unknown) => (option == null ? '' : String(option));

export function Combobox<T>(props: ComboboxProps<T>) {
  const {
    options,
    getOptionLabel = defaultGetOptionLabel,
    getOptionKey,
    isOptionEqualToValue = (option: T, value: T) => option === value,
    filterOptions,
    renderOption,
    onInputChange,
    freeSolo,
    loading,
    loadingText = 'Loading…',
    noOptionsText = 'No options',
    disableClearable,
    placeholder,
    disabled,
    invalid,
    autoFocus,
    id,
    className,
    inputClassName,
    inputRef,
    onKeyDown,
    onBlur,
    endAdornment,
  } = props;
  const [generatedId] = useState(() => `combobox-${++comboboxCount}`);
  const listboxId = `${id ?? generatedId}-listbox`;
  const selectedValues: T[] = props.multiple ? props.value : props.value == null ? [] : [props.value];
  const singleValueLabel = !props.multiple && props.value != null ? getOptionLabel(props.value) : '';

  const [open, setOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [uncontrolledInputValue, setUncontrolledInputValue] = useState(singleValueLabel);
  const inputValue = props.inputValue ?? uncontrolledInputValue;
  const anchorRef = useRef<HTMLDivElement>(null);
  const listboxRef = useRef<HTMLDivElement>(null);

  const setInputValue = (value: string, reason: ComboboxInputChangeReason) => {
    setUncontrolledInputValue(value);
    onInputChange?.(value, reason);
  };

  useEffect(() => {
    if (!props.multiple && props.inputValue === undefined && !open) {
      setUncontrolledInputValue(singleValueLabel);
    }
  }, [open, props.inputValue, props.multiple, singleValueLabel]);

  const filteredOptions = useMemo(() => {
    const optionList = [...options];
    if (filterOptions) {
      return filterOptions(optionList, inputValue);
    }
    const query = inputValue.trim().toLowerCase();
    if (!query || (!props.multiple && query === singleValueLabel.toLowerCase())) {
      return optionList;
    }
    return optionList.filter(option => getOptionLabel(option).toLowerCase().includes(query));
  }, [options, filterOptions, inputValue, props.multiple, singleValueLabel, getOptionLabel]);

  useEffect(() => {
    setHighlightedIndex(filteredOptions.length > 0 && !freeSolo ? 0 : -1);
  }, [filteredOptions, freeSolo]);

  useEffect(() => {
    if (highlightedIndex < 0) {
      return;
    }
    listboxRef.current
      ?.querySelector(`[data-option-index="${highlightedIndex}"]`)
      ?.scrollIntoView?.({ block: 'nearest' });
  }, [highlightedIndex]);

  const isSelected = (option: T) => selectedValues.some(value => isOptionEqualToValue(option, value));

  const selectOption = (option: T) => {
    if (props.multiple) {
      props.onChange(
        isSelected(option)
          ? props.value.filter(value => !isOptionEqualToValue(option, value))
          : [...props.value, option],
      );
      setInputValue('', 'reset');
      return;
    }
    props.onChange(option);
    setInputValue(getOptionLabel(option), 'reset');
    setOpen(false);
  };

  const commitFreeSolo = () => {
    const text = inputValue.trim();
    if (!text) {
      return;
    }
    selectOption(text as unknown as T);
  };

  const clear = () => {
    if (props.multiple) {
      props.onChange([]);
    } else {
      props.onChange(null);
    }
    setInputValue('', 'clear');
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    onKeyDown?.(event);
    if (event.defaultPrevented) {
      return;
    }
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setOpen(true);
        setHighlightedIndex(index => Math.min(index + 1, filteredOptions.length - 1));
        break;
      case 'ArrowUp':
        event.preventDefault();
        setOpen(true);
        setHighlightedIndex(index => Math.max(index - 1, 0));
        break;
      case 'Enter':
        if (open && highlightedIndex >= 0 && filteredOptions[highlightedIndex] !== undefined) {
          event.preventDefault();
          selectOption(filteredOptions[highlightedIndex]);
        } else if (freeSolo) {
          event.preventDefault();
          commitFreeSolo();
        }
        break;
      case 'Escape':
        if (open) {
          event.preventDefault();
          event.stopPropagation();
          setOpen(false);
        }
        break;
      case 'Backspace':
        if (props.multiple && !inputValue && props.value.length > 0) {
          props.onChange(props.value.slice(0, -1));
        }
        break;
      default:
        break;
    }
  };

  const showClear = !disableClearable && !disabled && (selectedValues.length > 0 || (freeSolo && !!inputValue));

  return (
    <PopoverPrimitive.Root open={open && !disabled} onOpenChange={setOpen}>
      <PopoverPrimitive.Anchor asChild>
        <div
          ref={anchorRef}
          className={cn(
            'flex min-h-9 w-full flex-wrap items-center gap-1 rounded-auditor border border-line-strong bg-surface px-2 py-1',
            'text-sm text-ink focus-within:border-brand focus-within:ring-2 focus-within:ring-brand/20',
            invalid && 'border-critical ring-critical/20',
            disabled && 'cursor-not-allowed bg-canvas opacity-60',
            className,
          )}
        >
          {props.multiple
            && props.value.map(option => {
              const onDelete = () => props.onChange(props.value.filter(value => !isOptionEqualToValue(option, value)));
              const key = getOptionKey ? getOptionKey(option) : getOptionLabel(option);
              return props.renderTag ? (
                <span key={key}>{props.renderTag(option, onDelete)}</span>
              ) : (
                <Chip key={key} size="small" onDelete={disabled ? undefined : onDelete}>
                  {getOptionLabel(option)}
                </Chip>
              );
            })}
          <input
            ref={inputRef}
            id={id}
            role="combobox"
            aria-expanded={open}
            aria-controls={listboxId}
            aria-autocomplete="list"
            aria-invalid={invalid || undefined}
            aria-label={props['aria-label']}
            aria-activedescendant={open && highlightedIndex >= 0 ? `${listboxId}-${highlightedIndex}` : undefined}
            autoComplete="off"
            autoFocus={autoFocus}
            disabled={disabled}
            placeholder={props.multiple && props.value.length > 0 ? undefined : placeholder}
            value={inputValue}
            className={cn(
              'h-7 min-w-16 flex-1 border-0 bg-transparent px-1 text-sm text-ink outline-hidden placeholder:text-ink-subtle',
              'disabled:cursor-not-allowed',
              inputClassName,
            )}
            onChange={event => {
              const next = event.target.value;
              setInputValue(next, 'input');
              setOpen(true);
              // Clearing the whole field also clears the selection (MUI Autocomplete behavior).
              if (!props.multiple && !disableClearable && next === '' && props.value != null) {
                props.onChange(null);
              }
            }}
            onFocus={() => setOpen(true)}
            onClick={() => setOpen(true)}
            onBlur={() => {
              if (freeSolo && !props.multiple) {
                commitFreeSolo();
              } else if (!props.multiple && props.inputValue === undefined) {
                setUncontrolledInputValue(singleValueLabel);
              }
              onBlur?.();
            }}
            onKeyDown={handleKeyDown}
          />
          {loading && <Spinner className="text-ink-muted" label="Loading options" />}
          {endAdornment}
          {showClear && (
            <button
              type="button"
              aria-label="Clear"
              tabIndex={-1}
              className="inline-flex size-6 items-center justify-center rounded-auditor text-ink-subtle hover:bg-canvas hover:text-ink"
              onMouseDown={event => event.preventDefault()}
              onClick={clear}
            >
              <X className="size-3.5" />
            </button>
          )}
          {!freeSolo && (
            <button
              type="button"
              aria-label={open ? 'Close options' : 'Open options'}
              tabIndex={-1}
              disabled={disabled}
              className="inline-flex size-6 items-center justify-center rounded-auditor text-ink-subtle hover:bg-canvas hover:text-ink"
              onMouseDown={event => event.preventDefault()}
              onClick={() => setOpen(current => !current)}
            >
              <ChevronDown className={cn('size-4 transition-transform', open && 'rotate-180')} />
            </button>
          )}
        </div>
      </PopoverPrimitive.Anchor>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          align="start"
          sideOffset={4}
          className={cn(
            'z-menu w-popover-trigger overflow-hidden rounded-auditor border border-line bg-surface text-ink',
            'shadow-auditor-overlay outline-hidden',
            freeSolo && filteredOptions.length === 0 && 'hidden',
          )}
          onOpenAutoFocus={event => event.preventDefault()}
          onCloseAutoFocus={event => event.preventDefault()}
          onInteractOutside={event => {
            if (anchorRef.current?.contains(event.target as Node)) {
              event.preventDefault();
            }
          }}
        >
          <div ref={listboxRef} id={listboxId} role="listbox" className="m-0 max-h-80 overflow-y-auto p-1">
            {loading && filteredOptions.length === 0 && (
              <div className="px-2 py-1.5 text-sm text-ink-muted">{loadingText}</div>
            )}
            {!loading && filteredOptions.length === 0 && !freeSolo && (
              <div className="px-2 py-1.5 text-sm text-ink-muted">{noOptionsText}</div>
            )}
            {filteredOptions.map((option, index) => {
              const selected = isSelected(option);
              return (
                <div
                  key={getOptionKey ? getOptionKey(option) : `${getOptionLabel(option)}-${index}`}
                  id={`${listboxId}-${index}`}
                  role="option"
                  aria-selected={selected}
                  data-option-index={index}
                  data-highlighted={index === highlightedIndex || undefined}
                  className={cn(
                    'cursor-pointer rounded-auditor px-2 py-1.5 text-sm',
                    'data-[highlighted]:bg-brand-subtle aria-selected:font-medium aria-selected:text-brand',
                  )}
                  onMouseDown={event => event.preventDefault()}
                  onMouseMove={() => setHighlightedIndex(index)}
                  onClick={() => selectOption(option)}
                >
                  {renderOption ? renderOption(option, { selected }) : getOptionLabel(option)}
                </div>
              );
            })}
          </div>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}
